<template>
    <div>
      <b-overlay :show="updating">
        <b-modal
          id="order-template-export"
          @ok="submitForm"
          header-class="p-0"
          footer-class="d-flex justify-content-center border-0"
          content-class="custom-model-wrap"
        >
          <template #modal-title>
            <div class="title-block text-center p-2">{{ $translate('language_settings') }}</div>
            <div class="close-btn" @click="cancel()">x</div>
          </template>
          <form class="my-3 py-3" @submit.stop.prevent="submitForm">
            <b-row>
              <a id="export-sample-file-btn" href="javascript:void(0)" title="Import" class="decoration-off text-success" @click="exportSampleFile">
                <i class="fas fa-file-excel fa-lg me-2"></i>{{ $translate('download_language_setting_file') }}
              </a>
            </b-row>
            <hr />
            <b-row>
              <b-col cols="12" class="mb-3">
                <label for="file-input"><strong>{{ $translate('upload_language_file') }}</strong></label>
                <input type="file" id="file-input" @change="handleFileUpload" class="form-control mt-2" required />
              </b-col>
            </b-row>
          </form>
          <template #modal-footer="{ ok, cancel }">
            <b-button id="import-btn" size="sm" variant="info" @click="ok()">{{ $translate('import') }}</b-button>
            <b-button id="cancel-btn" size="sm" variant="info" @click="cancel()">{{ $cancelLabel() }}</b-button>
          </template>
        </b-modal>
  
        <b-card :title="$translate('language_settings')" class="inner-page crm-setting p-3 form-sample">
          <b-card-body>
            <b-row>
              <b-col cols="12" class="text-end">
                <button id="download-btn" v-if="hasPermissionUpdate" @click="openModal()" class="btn btn mt-4 btn-info btn-primary">
                  <img :src="download" alt="download icon" />
                </button>
              </b-col>
            </b-row>
  
            <b-form class="p-0" @submit.prevent="handleSubmit">
              <b-row class="my-2">
                <b-col cols="2"></b-col>
                <b-col cols="5">
                  <b><u> English</u></b>
                </b-col>
                <b-col cols="5">
                  <b><u>Español</u></b>
                </b-col>
              </b-row>
              <hr />
              <div v-for="(translations, key) in visibleTranslations" :key="key" class="mb-3">
                <b-row>
                  <b-col cols="2">
                    <h5 class="mb-1">{{ translations }}</h5>
                  </b-col>
                  <b-col v-for="(lang, langKey) in labels" :key="langKey" cols="5">
                    <b-form-group label-cols="4" class="mb-1">
                      <b-form-input
                        :value="labels[langKey][key]"
                        @input="updateValue($event, langKey, key)"
                        :placeholder="`Enter ${langKey} translation for ${translations}`"
                        class="w-100"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
              <div v-if="loading" class="text-center mb-3">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <!-- <span>Loading...</span> -->
              </div>
              <b-col class="text-center my-4">
                <button id="update-settings-btn" v-if="hasPermissionUpdate" type="submit" class="btn btn mt-4 btn-info btn-fw me-2 btn-primary">
                  {{ $updateLabel() }}
                </button>
              </b-col>
            </b-form>
  
            <div ref="infiniteScrollTrigger" class="infinite-scroll-trigger"></div>
          </b-card-body>
        </b-card>
      </b-overlay>
    </div>
  </template>
  
  <script>
import popupMixins from '@/mixins/popupMixins';
import permissionsMixins from '@/mixins/permissionsMixins';
import download from '@/assets/images/icon/import.svg';

export default {
  name: 'crmSettings',
  mixins: [popupMixins, permissionsMixins],
  data: () => ({
    download: download,
    formValues: {},
    labels: {},
    translations: "",
    updating: false,
    visibleTranslations: {},
    chunkSize: 500,
    currentChunk: 0,
    observer: null,
    loading : false,
  }),
  computed: {
    componentName() {
      return this.$translate('language_settings');
    },
  },
  async mounted() {
   
    await this.initializeFormValues();
    
    this.loadNextChunk();

    // Set up the IntersectionObserver for lazy loading
    this.observer = new IntersectionObserver(this.onIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    if (this.$refs.infiniteScrollTrigger) {
      this.observer.observe(this.$refs.infiniteScrollTrigger);
    }
  },
  beforeDestroy() {
    if (this.observer && this.$refs.infiniteScrollTrigger) {
      this.observer.unobserve(this.$refs.infiniteScrollTrigger);
    }
  },
  methods: {
    handleFileUpload(event) {
      this.translations = event.target.files[0];
    },
    async initializeFormValues() {
      const languages = this.$store.state.languages;
      this.formValues = Object.keys(languages).reduce((acc, lang) => {
        acc[lang] = { ...languages[lang] };
        return acc;
      }, {});
      this.labels = Object.keys(languages).reduce((acc, lang) => {
        acc[lang] = { ...languages[lang] };
        return acc;
      }, {});
    },
    updateValue(event, langKey, key) {
      this.formValues[langKey][key] = event;
    },
    async handleSubmit() {
      this.updating = true;
      try {
        await this.$store.dispatch('setLanguage', this.formValues);
        this.genericToast(this.componentName, this.$translate('updated'), 'success', `${this.componentName} ${this.$translate('update_alert')}`);
        this.$nextTick(() => {
          window.location.reload();
        });
      } catch (error) {
        this.doToast(this.$translate('error'), error.message, 'danger');
        console.log(error);
      } finally {
        this.updating = false;
      }
    },
    async openModal() {
      this.$bvModal.show('order-template-export');
    },
    async exportSampleFile() {
      try {
        this.updating = true;
        await this.$store.dispatch('languages/sampleExcel');
      } catch (error) {
        this.doToast(this.$translate('error'), error.message, 'danger');
      } finally {
        this.updating = false;
      }
    },
    async submitForm() {
      const formData = new FormData();
      formData.append('translations', this.translations);

      try {
        this.updating = true;
        await this.$store.dispatch('languages/excelImport', formData);
        this.genericToast(this.componentName, this.$translate('updated'), 'success', `${this.componentName} ${this.$translate('update_alert')}`);
        this.$nextTick(() => {
          window.location.reload();
        });
      } catch (error) {
        this.doToast(this.$translate('error'), error.message, 'danger');
        this.updating = false;
      }
    },
    loadNextChunk() {
        this.loading = true; // Show loader when loading new chunk
        const keys = Object.keys(this.labels.en);
        const start = this.currentChunk * this.chunkSize;
        const end = Math.min(start + this.chunkSize, keys.length); // Ensure we don't go beyond the length of keys
        const nextChunk = keys.slice(start, end);

        nextChunk.forEach((key) => {
            this.$set(this.visibleTranslations, key, this.labels.en[key]);
        });

        this.currentChunk += 1;

        if (end >= keys.length) {
            this.loading = false;
            if (this.observer && this.$refs.infiniteScrollTrigger) {
            this.observer.unobserve(this.$refs.infiniteScrollTrigger);
            }
        }
        // Hide loader when loading is complete
        },

    onIntersection(entries) {
      const entry = entries[0];
      if (entry.isIntersecting) {
        this.loadNextChunk();
      }
    },
  },
};
</script>
